import { Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ROWS_PER_PAGE } from "../App";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import { DateFieldComponent } from "../components/DatePicker";
import { SelectFieldComponent, TextFieldComponent } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Holiday from "../models/Holiday";
import { default as holidayService, default as HolidayService } from "../services/HolidayService";
import { countries, Country } from "../utils/Countries";
import OpsDateUtils from "../utils/OpsDateUtils";
import RoleUtils from "../utils/RoleUtils";
import { Box } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { useLocation } from "react-router-dom";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import useTableHeight from "../hooks/useTableHeight";

import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { ColorUtils } from "../utils/ColorUtils";
import { useStyles as formStyles } from "../components/Main/formStyles";

export default function HolidayList() {
  const { t } = useTranslation();

  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | undefined>(undefined);
  const [country, setCountry] = useState<string>("");
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const countryFilter = searchParams.get("country");
  const [selectedEditElement, setSelectedEditElement] = useState<Holiday | undefined>(undefined);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const customStyles = formStyles();

  const {updateUrl} = useUpdateUrl(Routes.HOLIDAYS, searchParams);

  useEffect(() => {
    if (countryFilter !== null && countryFilter !== "") {
      setCountry(countryFilter);
    } else {
      if (country !== "") searchParams.set("country", country);
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnDeleteUser = async () => {
    if (!selectedHoliday) return;

    try {
      await holidayService.delete(selectedHoliday);
      setSelectedHoliday(undefined);
      NotificationService.info("Holiday deleted successfully");
      setUpdate(Date.now());
    } catch (error) {
      NotificationService.error("Error trying to delete a holiday.");
    }
  };

  const handleOnCreate = (instance: Holiday) => {
    return HolidayService.create(instance)
      .then(() => {
        NotificationService.info("Holiday created!");
        return true;
      })
      .catch((error) => {
        NotificationService.error("Holiday with same date and country already created");
        return false;
      });
  };

  const handleOnUpdate = (instance: Holiday) => {
    return HolidayService.update(instance)
      .then(() => {
        NotificationService.info("Holiday updated!");
        return true;
      })
      .catch((error) => {
        NotificationService.error("Holiday with same date and country already created");
        return false;
      });
  };


  function getAdditionalActions(instance: Holiday): ActionsMenuEntryIcons[] {
    if (!RoleUtils.isAdmin()) return (
      [{
        title: t("N/A"),
        action: () => {},
      }]
    )
    const options = [
      {
        title: t("Delete"),
        action: () => setSelectedHoliday(instance),
        icon:<DeleteOutlineIcon/>
      }, 
      {
        title: t("Edit"),
        action: () => {
          setSelectedEditElement(instance);
          setIsEditing(true);
        },
        icon:<EditIcon/>
      },       
    ];

    return options;
  }

  function handleOnCancel(): void {
    setIsEditing(false)
  }  

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <AppToolbar title={t('Holidays')}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            borderRadius: '4px',
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: '1rem',
            border: `${ColorUtils.LIGHTER_GRAY} solid 1px`,
            boxSizing: 'inherit',
            boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",  
            }}>
            <FormControl style={{ minWidth: "20em" }} variant="outlined" size="small">
              <InputLabel id="select-label">Country</InputLabel>
              <Select
                name="country"
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={country ? country : ""}
                onChange={(event: any) => {
                  setCountry(event.target.value);
                  setUpdate(Date.now());
                  if (event.target.value !== "") {
                    searchParams.set("country", event.target.value);
                  } else {
                    searchParams.delete("country");
                  }
                  updateUrl();
                }}
                label="Country"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value={Country.ARGENTINA}>{Country.ARGENTINA}</MenuItem>
                <MenuItem value={Country.BOLIVIA}>{Country.BOLIVIA}</MenuItem>
                <MenuItem value={Country.CHILE}>{Country.CHILE}</MenuItem>
                <MenuItem value={Country.COLOMBIA}>{Country.COLOMBIA}</MenuItem>
                <MenuItem value={Country.COSTA_RICA}>{Country.COSTA_RICA}</MenuItem>
                <MenuItem value={Country.CUBA}>{Country.CUBA}</MenuItem>
                <MenuItem value={Country.ECUADOR}>{Country.ECUADOR}</MenuItem>
                <MenuItem value={Country.HONDURAS}>{Country.HONDURAS}</MenuItem>
                <MenuItem value={Country.MEXICO}>{Country.MEXICO}</MenuItem>
                <MenuItem value={Country.PANAMA}>{Country.PANAMA}</MenuItem>
                <MenuItem value={Country.PERU}>{Country.PERU}</MenuItem>
                <MenuItem value={Country.UNITED_STATES}>{Country.UNITED_STATES}</MenuItem>
                <MenuItem value={Country.URUGUAY}>{Country.URUGUAY}</MenuItem>
                <MenuItem value={Country.VENEZUELA}>{Country.VENEZUELA}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </AppToolbar>
      }
      bottomSection={
        <Box sx={{ "&>div:first-child": { boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" } }}>
          <TableBasedCrud<Holiday>
            headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
            buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
            cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
            dialogStyles={ customStyles.root }
            buttonContainer={ customButtonsContainer }
            gridButtonContainer={ gridButtonContainer }
            addButtonBuilder={ customButtonBuilder }
            maxHeight={calculateMaxHeight(RoleUtils.isAdmin())}
            stickyHeader
            stickyPagination
            elementName={"Holidays"}
            rowsPerPage={ROWS_PER_PAGE}
            forceUpdateOn={update}
            denseTable
            onDataFetch={(pageNumber, count) =>
              holidayService.findHolidaysByCountry(pageNumber, count, country).then((result: any) => result.getContent())
            }
            onCreate={RoleUtils.isAdmin() ? (holiday) => handleOnCreate(holiday) : undefined}
            onUpdate={RoleUtils.isAdmin() ? (holiday) => handleOnUpdate(holiday) : undefined}
            validateBeforeClosing
            buildAdditionalActions={(instance, refresh) => [
              {
                title: t("Delete"),
                action: () => setSelectedHoliday(instance),
              },
            ]}
            renderDialogContent={(register, errors, control) => (
              <>
                <TextFieldComponent propertyName={"description"} label={"Description"} required={true} />
                <DateFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"shortDate"}
                  label="Date"
                  views={["month", "date"]}
                  openTo={"date"}
                  required={true}
                  clearable
                  toUTC
                />
                <SelectFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"country"}
                  label={"Country"}
                  defaultValue={"ARGENTINA"}
                  required={false}
                  options={countries}
                />
              </>
            )}
            resetValue={{ description: "", country: "ARGENTINA", shortDate: new Date() }}
            tableColumns={[
              {
                header: t("Description"),
                content: (instance) => <>{instance.description}</>,
              },
              {
                header: t("Date"),
                content: (instance) => <>{OpsDateUtils.formatUTCAsDDMMMYYY(instance.shortDate)}</>,
              },
              {
                header: t("Country"),
                content: (instance) => <>{instance.country ? instance.country : "-"}</>,
              },
              {
                header: t("Actions"),
                content: (instance) => {
                  const actions = getAdditionalActions(instance)
                  return (<>
                    {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                    </>
                  )
                },
              },
            ]}
            hideActions={true}  // Hide default Actions column
          />

          {/* Dialog to delete holidays */}
          <AppDialog
            open={!!selectedHoliday}
            onClose={() => setSelectedHoliday(undefined)}
            title={"Delete"}
            content={
              <Typography>
                {`The next holiday is going to be eliminated: ${selectedHoliday?.description}. 
              Do you want to confirm this action?`}
              </Typography>
            }
            buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteUser() }]}
          />


      {/* Copy of the Dialog to Edit -- To use with Edit Action Icon */}    
      <EditDialog<Holiday>
        headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
        buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
        cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
        dialogStyles={ customStyles.root }
        elementName={"Assignation"}
        dialogPosition={DialogPosition.Center}
        currentInstance={selectedEditElement}
        newItemDialogOpen={isEditing}
        onCreate={RoleUtils.isAdmin() ? (holiday) => handleOnCreate(holiday) : undefined}
        onUpdate={RoleUtils.isAdmin() ? (holiday) => handleOnUpdate(holiday) : undefined}
        onCancel={handleOnCancel}
        renderDialogContent={(register, errors, control) => (
          <>
            <TextFieldComponent propertyName={"description"} label={"Description"} required={true} />
            <DateFieldComponent
              control={control}
              register={register}
              errors={errors}
              propertyName={"shortDate"}
              label="Date"
              views={["month", "date"]}
              openTo={"date"}
              required={true}
              clearable
              toUTC
            />
            <SelectFieldComponent
              control={control}
              register={register}
              errors={errors}
              propertyName={"country"}
              label={"Country"}
              defaultValue={"ARGENTINA"}
              required={false}
              options={countries}
            />
          </>
        )}
        setNewInstanceDialogOpen={() => {}}
        setLastUpdateTimestamp={() => {}}
      />                 
        </Box>
      }
    />
  );
}
